import React, { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChefHat, Mail, Lock, User, Loader2, ArrowRight, CheckCircle2 } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { formatApiError } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const LoginPage = () => {
  const isLogin = true; // inscription publique désactivée : accès par invitation uniquement
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  const { user, login, register } = useAuth();
  const navigate = useNavigate();

  if (user) {
    return <Navigate to={user.is_manager ? '/gerer' : '/'} replace />;
  }

  const handleForgot = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/forgot-password`, { email });
      setForgotSent(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      let loggedUser;
      if (isLogin) {
        loggedUser = await login(email, password);
      } else {
        loggedUser = await register(email, password, name);
      }
      navigate(loggedUser?.is_manager ? '/gerer' : '/');
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex" data-testid="login-page">
      {/* Left side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <img
          src="https://static.prod-images.emergentagent.com/jobs/b2e6531b-16ef-4779-8f08-73a25fc57a21/images/43bde03328914f764b514ad04c66d22607e2f4187731cea2a5b57abfbb32a416.png"
          alt="Kitchen"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-black/70 via-black/50 to-transparent" />
        <div className="relative z-10 p-12 flex flex-col justify-between">
          <div>
            <div className="flex items-center gap-3 mb-8">
              <img src="/logo.png" alt="Chef's Connect" className="w-12 h-12 rounded-xl" />
              <h1 className="font-heading font-bold text-2xl text-white">Chef's Connect</h1>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <h2 className="font-heading text-4xl lg:text-5xl font-bold text-white mb-4 tracking-tight">
                Gérez votre mise en place{' '}
                <span className="gradient-text">comme un chef</span>
              </h2>
              <p className="text-white/80 text-lg max-w-md">
                Connecté à Odoo 18, suivez vos tâches, recettes et prévisions de vente en temps réel.
              </p>
            </motion.div>
          </div>

          <div className="flex gap-4">
            <div className="glass rounded-2xl p-4 flex-1">
              <div className="text-3xl font-bold text-white mb-1">150+</div>
              <div className="text-white/70 text-sm">Recettes synchronisées</div>
            </div>
            <div className="glass rounded-2xl p-4 flex-1">
              <div className="text-3xl font-bold text-neon-pink mb-1">98%</div>
              <div className="text-white/70 text-sm">Efficacité équipe</div>
            </div>
          </div>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="w-full max-w-md"
        >
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center gap-3 justify-center mb-8">
            <img src="/logo.png" alt="Chef's Connect" className="w-12 h-12 rounded-xl" />
            <h1 className="font-heading font-bold text-2xl text-gray-900">Chef's Connect</h1>
          </div>

          <div className="bg-white border border-gray-200 rounded-3xl p-8">
            {forgotMode ? (
              <div data-testid="forgot-password-form">
                <div className="text-center mb-8">
                  <h2 className="font-heading text-2xl font-bold text-gray-900 mb-2">Mot de passe oublié ?</h2>
                  <p className="text-gray-500">Entrez votre email, nous vous enverrons un lien de réinitialisation.</p>
                </div>
                {forgotSent ? (
                  <div className="text-center space-y-4" data-testid="forgot-password-sent">
                    <CheckCircle2 className="w-14 h-14 text-green-600 mx-auto" />
                    <p className="text-gray-700">Si un compte existe avec <span className="font-medium">{email}</span>, un email vient de vous être envoyé. Vérifiez votre boîte de réception (et vos spams).</p>
                  </div>
                ) : (
                  <form onSubmit={handleForgot} className="space-y-5">
                    <div className="space-y-2">
                      <Label htmlFor="forgot-email" className="text-gray-700">Email</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                        <Input
                          id="forgot-email"
                          type="email"
                          placeholder="chef@restaurant.com"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          className="pl-10 h-12 rounded-xl bg-gray-100 border-gray-200 text-gray-900 placeholder:text-gray-400 focus:border-neon-pink focus:ring-neon-pink/20"
                          data-testid="forgot-email-input"
                          required
                          autoFocus
                        />
                      </div>
                    </div>
                    {error && (
                      <div className="bg-red-500/10 border border-red-500/30 text-red-600 px-4 py-3 rounded-xl text-sm" data-testid="forgot-error">{error}</div>
                    )}
                    <Button
                      type="submit"
                      className="w-full h-12 rounded-full bg-neon-pink hover:bg-neon-pink/90 text-gray-900 font-semibold text-base shadow-neon-pink neon-glow-pink"
                      disabled={loading}
                      data-testid="forgot-submit-btn"
                    >
                      {loading ? <Loader2 className="animate-spin" size={20} /> : <>Envoyer le lien<ArrowRight className="ml-2" size={18} /></>}
                    </Button>
                  </form>
                )}
                <div className="mt-6 text-center">
                  <button
                    type="button"
                    onClick={() => { setForgotMode(false); setForgotSent(false); setError(''); }}
                    className="text-neon-pink hover:text-neon-pink/80 font-medium transition-colors"
                    data-testid="back-to-login-btn"
                  >
                    Retour à la connexion
                  </button>
                </div>
              </div>
            ) : (
            <>
            <div className="text-center mb-8">
              <h2 className="font-heading text-2xl font-bold text-gray-900 mb-2">
                {isLogin ? 'Bon retour, Chef !' : 'Rejoindre la brigade'}
              </h2>
              <p className="text-gray-500">
                {isLogin ? 'Connectez-vous pour continuer' : 'Créez votre compte'}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              {!isLogin && (
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-gray-700">Nom</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                    <Input
                      id="name"
                      type="text"
                      placeholder="Chef Marco"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="pl-10 h-12 rounded-xl bg-gray-100 border-gray-200 text-gray-900 placeholder:text-gray-400 focus:border-neon-pink focus:ring-neon-pink/20"
                      data-testid="register-name-input"
                      required={!isLogin}
                    />
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="email" className="text-gray-700">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                  <Input
                    id="email"
                    type="email"
                    placeholder="chef@restaurant.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 h-12 rounded-xl bg-gray-100 border-gray-200 text-gray-900 placeholder:text-gray-400 focus:border-neon-pink focus:ring-neon-pink/20"
                    data-testid="login-email-input"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="text-gray-700">Mot de passe</Label>
                  {isLogin && (
                    <button
                      type="button"
                      onClick={() => { setForgotMode(true); setError(''); }}
                      className="text-sm text-neon-pink hover:text-neon-pink/80 font-medium transition-colors"
                      data-testid="forgot-password-link"
                    >
                      Mot de passe oublié ?
                    </button>
                  )}
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 h-12 rounded-xl bg-gray-100 border-gray-200 text-gray-900 placeholder:text-gray-400 focus:border-neon-pink focus:ring-neon-pink/20"
                    data-testid="login-password-input"
                    required
                  />
                </div>
              </div>

              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-red-500/10 border border-red-500/30 text-red-600 px-4 py-3 rounded-xl text-sm"
                  data-testid="auth-error"
                >
                  {error}
                </motion.div>
              )}

              <Button
                type="submit"
                className="w-full h-12 rounded-full bg-neon-pink hover:bg-neon-pink/90 text-gray-900 font-semibold text-base shadow-neon-pink neon-glow-pink"
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading ? (
                  <Loader2 className="animate-spin" size={20} />
                ) : (
                  <>
                    <span>{isLogin ? 'Se connecter' : "S'inscrire"}</span>
                    <ArrowRight className="ml-2" size={18} />
                  </>
                )}
              </Button>
            </form>

            <p className="mt-6 text-center text-sm text-gray-500" data-testid="invite-only-hint">
              Pas encore de compte ? L'accès se fait uniquement sur invitation du chef.
            </p>
            </>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default LoginPage;
